import { createSessionLatencyTrace } from "@/lib/sessionLatency";
import type { SessionMode } from "@/lib/sessionRouting";
import { useCallback, useRef, useState } from "react";

type ConnectionTimingLogger = {
  info: (message: string, data?: unknown) => void;
  warn: (message: string, data?: unknown) => void;
  error?: (message: string, data?: unknown) => void;
};

type UseLiveKitConnectionTimingInput = {
  selectedLanguageCode: string;
  selectedVoice: string;
  avatarId?: string;
  noAvatar: boolean;
  selectedModel?: "pro" | "stealth" | "legacy";
  logger: ConnectionTimingLogger;
};

export function useLiveKitConnectionTiming({
  selectedLanguageCode,
  selectedVoice,
  avatarId,
  noAvatar,
  selectedModel,
  logger,
}: UseLiveKitConnectionTimingInput) {
  const [connectionElapsedSeconds, setConnectionElapsedSeconds] = useState<number>(0);
  const connectionStartedAtRef = useRef<number | null>(null);
  const connectionElapsedTimerRef = useRef<NodeJS.Timeout | null>(null);
  const latencyTraceRef = useRef<ReturnType<typeof createSessionLatencyTrace> | null>(null);
  const latencyAgentStateMarksRef = useRef<Set<string>>(new Set());
  const firstRemoteAudioMarkedRef = useRef(false);

  const clearConnectionElapsedTimer = useCallback(() => {
    if (connectionElapsedTimerRef.current) {
      clearInterval(connectionElapsedTimerRef.current);
      connectionElapsedTimerRef.current = null;
    }
  }, []);

  const updateConnectionElapsedTime = useCallback(() => {
    const startedAt = connectionStartedAtRef.current;
    if (startedAt === null) {
      return;
    }

    const elapsed = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
    setConnectionElapsedSeconds((prev) => (prev === elapsed ? prev : elapsed));
  }, []);

  const startConnectionElapsedTimer = useCallback(() => {
    if (connectionStartedAtRef.current === null) {
      connectionStartedAtRef.current = Date.now();
      setConnectionElapsedSeconds(0);
    }

    updateConnectionElapsedTime();

    if (connectionElapsedTimerRef.current) {
      return;
    }

    connectionElapsedTimerRef.current = setInterval(() => {
      updateConnectionElapsedTime();
    }, 250);
  }, [updateConnectionElapsedTime]);

  const freezeConnectionElapsedTimer = useCallback(() => {
    updateConnectionElapsedTime();
    clearConnectionElapsedTimer();
    connectionStartedAtRef.current = null;
  }, [clearConnectionElapsedTimer, updateConnectionElapsedTime]);

  const resetConnectionElapsedTimer = useCallback(() => {
    clearConnectionElapsedTimer();
    connectionStartedAtRef.current = null;
    setConnectionElapsedSeconds(0);
  }, [clearConnectionElapsedTimer]);

  const resetLatencyTrace = useCallback(() => {
    latencyTraceRef.current = null;
    latencyAgentStateMarksRef.current.clear();
    firstRemoteAudioMarkedRef.current = false;
  }, []);

  const startLatencyTrace = useCallback(
    (mode: SessionMode) => {
      const trace = createSessionLatencyTrace({
        logger,
        scope: "start-speaking",
        clientStartMs: Date.now(),
      });
      latencyTraceRef.current = trace;
      latencyAgentStateMarksRef.current.clear();
      firstRemoteAudioMarkedRef.current = false;
      trace.mark("start_speaking_clicked", {
        mode,
        languageCode: selectedLanguageCode,
        selectedVoice,
        avatarId: avatarId || null,
        noAvatar,
        selectedModel,
      });
      return trace;
    },
    [avatarId, logger, noAvatar, selectedLanguageCode, selectedModel, selectedVoice]
  );

  const markLatencyPhase = useCallback((phase: string, data?: Record<string, unknown>) => {
    latencyTraceRef.current?.mark(phase, data);
  }, []);

  return {
    connectionElapsedSeconds,
    latencyTraceRef,
    latencyAgentStateMarksRef,
    firstRemoteAudioMarkedRef,
    clearConnectionElapsedTimer,
    startConnectionElapsedTimer,
    freezeConnectionElapsedTimer,
    resetConnectionElapsedTimer,
    resetLatencyTrace,
    startLatencyTrace,
    markLatencyPhase,
  };
}
